// src/pages/admin/ApplicationDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getApplicationStatus,
  approveApplication,
  rejectApplication
} from '../../services/UserRolesService';
import { getListingById } from '../../services/ListingsService';
import { sendUserUpdateNotification } from '../../services/EmailNotificationService';
import './Admin.css';

function ApplicationDetail() {
  const { id: applicationId } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState(null);
  const [listingPreview, setListingPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // For rejection modal
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user is admin
  useEffect(() => {
    if (!loading && (!currentUser || !isAdmin)) {
      navigate('/');
    }
  }, [currentUser, isAdmin, loading, navigate]);

  // Fetch application details
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      try {
        const app = await getApplicationStatus(applicationId);
        if (!app) {
          setError("Application not found");
          setLoading(false);
          return;
        }

        setApplication(app);

        // Current flow: the pending listing is a real record linked by listingId.
        // Legacy applications stored the listing inline instead.
        if (app.listingId) {
          try {
            setListingPreview(await getListingById(app.listingId));
          } catch (listingErr) {
            console.error("Error fetching linked listing:", listingErr);
          }
        } else if (app.listing) {
          setListingPreview(app.listing);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching application details:", err);
        setError("Failed to load application details. Please try again later.");
        setLoading(false);
      }
    };

    if (currentUser && isAdmin) {
      fetchApplicationDetails();
    }
  }, [applicationId, currentUser, isAdmin]);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Nested listing timestamps may still be raw Firestore Timestamps
  const formatEventDate = (value) => {
    if (!value) return null;
    const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleString('en-CA');
  };

  const formatPrice = (listing) => {
    if (listing.isFreeEvent) return 'Free';
    if (listing.isContactForPrice) return 'Contact for pricing';
    return `$${listing.price}`;
  };

  // Handle application approval
  const handleApprove = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await approveApplication(applicationId);

      // Notify the provider; approval itself already succeeded, so don't fail on email issues
      try {
        await sendUserUpdateNotification({
          userEmail: application.email,
          userName: application.displayName || application.businessName,
          message: listingPreview
            ? `Great news! Your provider application has been approved and your listing "${listingPreview.title}" is now live on RefoundEd. Sign in to manage it from your provider dashboard.`
            : 'Great news! Your provider application has been approved. Sign in to start adding listings.'
        });
      } catch (emailErr) {
        console.error("Error sending approval notification:", emailErr);
      }

      setSuccess(listingPreview
        ? "Application approved and listing published"
        : "Application approved successfully");
      setIsSubmitting(false);

      // Refresh data after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
    } catch (err) {
      console.error("Error approving application:", err);
      setError("Failed to approve application. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Open rejection modal
  const openRejectionModal = () => {
    setShowRejectionModal(true);
  };

  // Close rejection modal
  const closeRejectionModal = () => {
    setShowRejectionModal(false);
    setRejectionReason('');
  };

  // Handle application rejection
  const handleReject = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      await rejectApplication(applicationId, rejectionReason);

      // Notify the provider; rejection already succeeded, so don't fail on email issues
      try {
        await sendUserUpdateNotification({
          userEmail: application.email,
          userName: application.displayName || application.businessName,
          message: `Unfortunately, your provider application was not approved.${rejectionReason ? ` Reason: ${rejectionReason}` : ''} If you believe this was in error or have updated information, please contact our support team.`
        });
      } catch (emailErr) {
        console.error("Error sending rejection notification:", emailErr);
      }

      setSuccess("Application rejected");
      setIsSubmitting(false);
      closeRejectionModal();
      
      // Refresh data after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
    } catch (err) {
      console.error("Error rejecting application:", err);
      setError("Failed to reject application. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="admin-container">Loading application details...</div>;
  }

  if (error && !application) {
    return (
      <div className="admin-container">
        <div className="error-message">{error}</div>
        <Link to="/admin" className="back-button">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Review Service Provider Application</h1>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="application-detail">
        <div className="application-header">
          <h2>{application.businessName}</h2>
          <span className="badge">{application.businessType}</span>
        </div>
        
        <div className="application-meta">
          <div className="application-meta-group">
            <p className="detail-label">Applicant Name</p>
            <p className="detail-value">{application.displayName}</p>
            
            <p className="detail-label">Email</p>
            <p className="detail-value">{application.email}</p>

            {application.phoneNumber && (
              <>
                <p className="detail-label">Phone Number</p>
                <p className="detail-value">{application.phoneNumber}</p>
              </>
            )}
          </div>

          <div className="application-meta-group">
            <p className="detail-label">Application Date</p>
            <p className="detail-value">{formatDate(application.submittedAt)}</p>

            {application.address && (
              <>
                <p className="detail-label">Business Address</p>
                <p className="detail-value">{application.address}</p>
              </>
            )}

            {application.website && (
              <>
                <p className="detail-label">Website</p>
                <p className="detail-value">
                  <a 
                    href={application.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {application.website}
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
        
        <div className="application-detail-section">
          <p className="detail-label">Description</p>
          <p className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>{application.description}</p>
        </div>

        {application.additionalInfo && (
          <div className="application-detail-section">
            <p className="detail-label">Additional Information</p>
            <p className="detail-value">{application.additionalInfo}</p>
          </div>
        )}

        {listingPreview && (
          <div className="application-detail-section">
            <h3 style={{ marginBottom: '0.75rem' }}>First Listing (published automatically on approval)</h3>

            <p className="detail-label">Title</p>
            <p className="detail-value">{listingPreview.title}</p>

            <p className="detail-label">Category</p>
            <p className="detail-value">{(listingPreview.category || 'other').replace('_', ' ')}</p>

            <p className="detail-label">Price</p>
            <p className="detail-value">{formatPrice(listingPreview)}</p>

            <p className="detail-label">Location</p>
            {listingPreview.isVirtual ? (
              <p className="detail-value">Virtual{listingPreview.location ? ` — ${listingPreview.location}` : ''}</p>
            ) : (
              <div className="detail-value">
                {(listingPreview.locations?.length ? listingPreview.locations : [{ id: 'primary', address: listingPreview.location }]).map((loc, index) => (
                  <p key={loc.id || index} style={{ margin: '0.15rem 0' }}>{loc.address}</p>
                ))}
              </div>
            )}

            <p className="detail-label">Contact Email</p>
            <p className="detail-value">{listingPreview.contact}</p>

            <p className="detail-label">Social Media & Website</p>
            <div className="detail-value">
              {Object.entries(listingPreview.socialMedia || {})
                .filter(([, value]) => value && value.trim())
                .map(([platform, value]) => (
                  <p key={platform} style={{ margin: '0.15rem 0' }}>
                    <strong style={{ textTransform: 'capitalize' }}>{platform}:</strong>{' '}
                    {value.startsWith('http') ? (
                      <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
                    ) : (
                      value
                    )}
                  </p>
                ))}
            </div>

            {formatEventDate(listingPreview.eventStart) && (
              <>
                <p className="detail-label">Event Start</p>
                <p className="detail-value">{formatEventDate(listingPreview.eventStart)}</p>
              </>
            )}

            {formatEventDate(listingPreview.eventEnd) && (
              <>
                <p className="detail-label">Event End</p>
                <p className="detail-value">{formatEventDate(listingPreview.eventEnd)}</p>
              </>
            )}

            {listingPreview.maxAttendees && (
              <>
                <p className="detail-label">Maximum Attendees</p>
                <p className="detail-value">{listingPreview.maxAttendees}</p>
              </>
            )}

            {listingPreview.media?.length > 0 && (
              <>
                <p className="detail-label">Media ({listingPreview.media.length})</p>
                <div className="detail-value" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {listingPreview.media.map((item, index) => (
                    item.type === 'image' ? (
                      <a key={index} href={item.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={item.url}
                          alt={`Listing media ${index + 1}`}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px' }}
                        />
                      </a>
                    ) : (
                      <a key={index} href={item.url} target="_blank" rel="noopener noreferrer">
                        Video {index + 1}
                      </a>
                    )
                  ))}
                </div>
              </>
            )}

            {(application.listingId || application.publishedListingId) && (
              <p style={{ marginTop: '0.75rem' }}>
                <Link to={`/listings/${application.listingId || application.publishedListingId}`}>View listing page</Link>
              </p>
            )}
          </div>
        )}

        <div className="admin-actions">
          <Link to="/admin" className="back-button">
            Back to Applications
          </Link>
          
          <button 
            onClick={openRejectionModal} 
            className="reject-button"
            disabled={isSubmitting}
          >
            Reject Application
          </button>
          
          <button
            onClick={handleApprove}
            className="approve-button"
            disabled={isSubmitting}
          >
            {listingPreview ? 'Approve & Publish Listing' : 'Approve Application'}
          </button>
        </div>
      </div>
      
      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Reject Application</h3>
            </div>
            
            <div className="modal-body">
              <p>Please provide a reason for rejecting this application:</p>
              <textarea
                className="form-control"
                rows="4"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason (optional)"
              ></textarea>
            </div>
            
            <div className="modal-footer">
              <button onClick={closeRejectionModal} className="back-button">
                Cancel
              </button>
              
              <button 
                onClick={handleReject} 
                className="reject-button"
                disabled={isSubmitting}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationDetail;